<template>
  <button
    class="btn"
    :class="[`size-${size}`, variant && `variant-${variant}`]"
    :style="{
      '--btn-h': buttonHeight || undefined,
      '--btn-w': buttonWidth || undefined,
      '--btn-fs': textSize || undefined,
      '--btn-lh': lineHeight || undefined,
      '--icon-size': iconSize || undefined,
      '--btn-bg': backBg || undefined,
      '--btn-fg': frontBg || undefined,
      '--btn-tc': textColor || undefined,
    }"
    type="button"
    @click="handleClick"
    :aria-label="ariaLabel"
  >
    <span class="btn-bg" aria-hidden="true"></span>

    <span class="btn-sizer" aria-hidden="true">
      <template v-if="iconOnly">
        <img v-if="iconLink" :src="iconLink" alt="" class="icon" />
      </template>

      <template v-else>
        <template v-if="rightIcon">
          <span class="label">{{ text }}</span>
          <img v-if="iconLink" :src="iconLink" alt="" class="icon" />
        </template>
        <template v-else>
          <img v-if="iconLink" :src="iconLink" alt="" class="icon" />
          <span class="label">{{ text }}</span>
        </template>
      </template>
    </span>

    <span class="btn-inner" :class="{ 'btn-pressed': isClicked }">
      <template v-if="iconOnly">
        <img v-if="iconLink" :src="iconLink" alt="" class="icon" />
      </template>

      <template v-else>
        <template v-if="rightIcon">
          <span class="label">{{ text }}</span>
          <img v-if="iconLink" :src="iconLink" alt="" class="icon" />
        </template>
        <template v-else>
          <img v-if="iconLink" :src="iconLink" alt="" class="icon" />
          <span class="label">{{ text }}</span>
        </template>
      </template>
    </span>
  </button>
</template>

<script>
export default {
  name: "UtilButton",
  props: {
    text: { type: String, default: "Action" },
    iconLink: { type: String, required: false },
    clickFunction: { type: Function, required: true },
    rightIcon: { type: Boolean, default: false },
    iconOnly: { type: Boolean, default: false },

    // 🆕 Now supports xxs | xs | sm | md | lg
    size: { type: String, default: "md" },
    variant: { type: String, default: "primary" },

    frontBg: { type: [String, null], default: null },
    backBg: { type: [String, null], default: null },
    textColor: { type: [String, null], default: null },

    buttonHeight: { type: [String, null], default: null },
    buttonWidth: { type: [String, null], default: null },
    textSize: { type: [String, null], default: null },
    lineHeight: { type: [String, null], default: null },
    iconSize: { type: [String, null], default: null },

    ariaLabel: { type: String, default: "" },
  },
  data() {
    return { isClicked: false };
  },
  methods: {
    handleClick() {
      this.isClicked = true;
      setTimeout(() => (this.isClicked = false), 200);
      setTimeout(() => this.clickFunction && this.clickFunction(), 300);
    },
  },
};
</script>

<style scoped>
/* Defaults via CSS variables (mobile-first) */
.btn {
  --btn-h: 44px;
  --btn-w: 100%;
  --btn-fs: 16px;
  --btn-lh: 20px;
  --icon-size: 20px;
  --btn-px: 16px;
  --btn-bg: #000;
  --btn-fg: #fff;
  --btn-tc: #000;

  --press: 10%;
  --label-ws: nowrap;

  position: relative;
  display: inline-block;
  vertical-align: middle;
  height: var(--btn-h);
  width: var(--btn-w);
  padding: 0 16px;
  border: 0;
  border-radius: 8px;
  background: none;
  color: var(--btn-tc);
  cursor: pointer;
  text-align: center;
  box-shadow: none;
}
.btn-sizer {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: calc(var(--btn-h) - var(--press));
  padding: 0 var(--btn-px);
  visibility: hidden;
  pointer-events: none;
  box-sizing: border-box;
  max-width: 100%;
}

/* Plate and face */
.btn-bg {
  position: absolute;
  inset: 0;
  background: var(--btn-bg);
  border-radius: 8px;
  transform: translateY(var(--press));
  height: calc(100% - var(--press));
  transition: transform 0.1s ease-in-out;
}
.btn-inner {
  position: absolute;
  inset: 0 0 var(--press) 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 0 var(--btn-px);
  background: var(--btn-fg);
  border-radius: 8px;
  height: calc(100% - var(--press));
  transition: transform 0.1s ease-in-out;
  box-sizing: border-box;
}
.btn-pressed {
  transform: translateY(var(--press));
}

/* Label + Icon */
.label {
  font-family: "Poppins", sans-serif;
  font-weight: 700;
  font-size: var(--btn-fs);
  line-height: var(--btn-lh);
  white-space: var(--label-ws);
}
.icon {
  width: var(--icon-size);
  height: var(--icon-size);
}

/* ===================== */
/* Size variants (semantic)
   xxs | xs | sm | md | lg
/* ===================== */
.size-xxs {
  --btn-h: 22px;
  --btn-fs: 10px;
  --btn-lh: 14px;
  --icon-size: 12px;
}
.size-xs {
  --btn-h: 26px;
  --btn-fs: 12px;
  --btn-lh: 16px;
  --icon-size: 14px;
}
.size-sm {
  --btn-h: 32px;
  --btn-fs: 14px;
  --btn-lh: 18px;
  --icon-size: 18px;
}
.size-md {
  --btn-h: 44px;
  --btn-fs: 16px;
  --btn-lh: 20px;
  --icon-size: 20px;
}
.size-lg {
  --btn-h: 52px;
  --btn-fs: 18px;
  --btn-lh: 22px;
  --icon-size: 22px;
}

/* ===================== */
/* Color Variants */
/* ===================== */
.variant-primary {
  --btn-bg: #000;
  --btn-fg: #fff;
  --btn-tc: #000;
}
.variant-secondary {
  --btn-bg: #000;
  --btn-fg: #676767;
  --btn-tc: #fff;
}
.variant-ghost {
  --btn-bg: transparent;
  --btn-fg: transparent;
  --btn-tc: inherit;
}

/* ===================== */
/* Responsive refinements */
/* ===================== */
@media (min-width: 768px) {
  .size-xxs {
    --btn-h: 24px;
    --btn-fs: 11px;
    --btn-lh: 15px;
    --icon-size: 14px;
  }
  .size-xs {
    --btn-h: 28px;
    --btn-fs: 13px;
    --btn-lh: 18px;
    --icon-size: 16px;
  }
  .size-sm {
    --btn-h: 34px;
    --btn-fs: 15px;
    --btn-lh: 20px;
    --icon-size: 20px;
  }
  .size-md {
    --btn-h: 48px;
    --btn-fs: 17px;
    --btn-lh: 22px;
    --icon-size: 22px;
  }
  .size-lg {
    --btn-h: 60px;
    --btn-fs: 20px;
    --btn-lh: 26px;
    --icon-size: 26px;
  }
}

@media (min-width: 1024px) {
  .size-xxs {
    --btn-h: 26px;
    --btn-fs: 12px;
    --btn-lh: 16px;
    --icon-size: 16px;
  }
  .size-xs {
    --btn-h: 30px;
    --btn-fs: 14px;
    --btn-lh: 20px;
    --icon-size: 18px;
  }
  .size-sm {
    --btn-h: 36px;
    --btn-fs: 16px;
    --btn-lh: 22px;
    --icon-size: 22px;
  }
  .size-md {
    --btn-h: 52px;
    --btn-fs: 18px;
    --btn-lh: 24px;
    --icon-size: 24px;
  }
  .size-lg {
    --btn-h: 64px;
    --btn-fs: 22px;
    --btn-lh: 28px;
    --icon-size: 28px;
  }
}
</style>